import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { FiAlignCenter, FiAlignLeft, FiAlignRight, FiTrash2 } from "react-icons/fi";
import { fabric } from "fabric";
import editorStore from "../../stores/EditorStore";
import { BANNER_FONTS, BANNER_FONT_SIZES } from "./bannerConfig";

const ALIGNS = [
  { value: "left", Icon: FiAlignLeft },
  { value: "center", Icon: FiAlignCenter },
  { value: "right", Icon: FiAlignRight },
];

const fieldClass = "h-7 rounded border border-gray-300 bg-white px-1.5 text-xs text-gray-700 outline-none focus:border-brand";

/** Floating format bar under the selected text box. Drag it by the blue handle. */
const BannerTextBar = observer(() => {
  void editorStore.selectedElements;
  void editorStore.layersRevision;

  // Re-position on every canvas render (move, scale, zoom, pan)
  const [, setTick] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const canvas = editorStore.getCanvas();
  const activeObj = canvas?.getActiveObject() as fabric.Textbox | null | undefined;
  const isText = !!activeObj && (activeObj.type === "textbox" || activeObj.type === "i-text" || activeObj.type === "text");

  useEffect(() => {
    if (!canvas) return;
    let frame = 0;
    const onRender = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setTick(t => t + 1));
    };
    canvas.on("after:render", onRender);
    return () => { canvas.off("after:render", onRender); cancelAnimationFrame(frame); };
  }, [canvas]);

  // A newly selected box gets the bar back in its default spot
  useEffect(() => setOffset({ x: 0, y: 0 }), [activeObj]);

  if (!isText || !activeObj || !canvas) return null;

  const apply = (prop: string, value: any) => {
    (activeObj as any).set(prop, value);
    canvas.requestRenderAll();
    editorStore.refreshAfterEdit();
  };

  const fontFamily: string = (activeObj as any).fontFamily ?? BANNER_FONTS[0];
  const fontSize: number = Math.round((activeObj as any).fontSize ?? 0);
  const fill = String((activeObj as any).fill ?? "#000000");
  const alignIdx = Math.max(0, ALIGNS.findIndex(a => a.value === (activeObj as any).textAlign));
  const AlignIcon = ALIGNS[alignIdx].Icon;
  const sizes = BANNER_FONT_SIZES.includes(fontSize) ? BANNER_FONT_SIZES : [...BANNER_FONT_SIZES, fontSize].sort((a, b) => a - b);

  // calculate=true: cached coords lag behind while the box is being dragged
  const rect = activeObj.getBoundingRect(false, true);
  const left = rect.left + rect.width / 2 + offset.x;
  const top = rect.top + rect.height + 16 + offset.y;

  const onHandleDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onHandleMove = (e: React.PointerEvent) => {
    if (drag.current) setOffset({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };

  return (
    <div
      className="absolute z-20 flex -translate-x-1/2 items-stretch overflow-hidden rounded bg-white font-jakarta shadow-lg"
      style={{ left, top }}
      onMouseDown={e => e.stopPropagation()}
    >
      <div
        className="flex w-3 cursor-move items-center justify-center bg-brand text-[8px] leading-none text-white/80"
        onPointerDown={onHandleDown}
        onPointerMove={onHandleMove}
        onPointerUp={() => { drag.current = null; }}
        title="Drag"
      >
        ⋮
      </div>

      <div className="flex items-center gap-3 px-3 py-2">
        <label className="flex flex-col text-[9px] text-gray-400">
          Fonts
          <select className={`${fieldClass} w-36`} style={{ fontFamily }} value={fontFamily} onChange={e => apply("fontFamily", e.target.value)}>
            {!BANNER_FONTS.includes(fontFamily) && <option value={fontFamily}>{fontFamily}</option>}
            {BANNER_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
          </select>
        </label>

        <div className="h-8 w-px bg-gray-200" />

        <label className="flex flex-col text-[9px] text-gray-400">
          Size
          <select className={`${fieldClass} w-16`} value={fontSize} onChange={e => apply("fontSize", Number(e.target.value))}>
            {sizes.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>

        <div className="h-8 w-px bg-gray-200" />

        <label className="relative flex cursor-pointer flex-col items-center gap-1 text-[10px] text-gray-700">
          <span className="h-4 w-4 rounded-full border border-gray-300" style={{ background: fill }} />
          Color
          <input type="color" value={fill} onChange={e => apply("fill", e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" />
        </label>

        <button
          className="flex flex-col items-center gap-1 text-[10px] text-gray-700"
          onClick={() => apply("textAlign", ALIGNS[(alignIdx + 1) % ALIGNS.length].value)}
          title={`Align ${ALIGNS[alignIdx].value}`}
        >
          <AlignIcon size={16} />
          Align
        </button>

        <div className="h-8 w-px bg-gray-200" />

        <button className="flex flex-col items-center gap-1 text-[10px] text-gray-700 hover:text-red-600" onClick={() => editorStore.deleteSelected()}>
          <FiTrash2 size={16} />
          Remove
        </button>
      </div>
    </div>
  );
});

export default BannerTextBar;
